import { useState } from 'react';
import { Search, Mail, Users, Building, Folder, AlertTriangle, RefreshCw, Flag } from 'lucide-react';
const Main = () => {
    const [activeTab, setActiveTab] = useState("tasks")
    const crmBuckets = {
        renewals: {
            name: "Renewals",
            description: "Upcoming trademark renewals",
            count: 45,
            priority: "high",
            items: [
                { id: 1, name: "TechCorp", dueDate: "2024-03-15", type: "Trademark", status: "Due in 30 days" },
                { id: 2, name: "InnovateAI", dueDate: "2024-04-01", type: "Patent", status: "Due in 45 days" }
            ]
        },
        vmc: {
            name: "VMC",
            description: "Voluntary Metadata Contacts",
            count: 28,
            priority: "medium",
            items: []
        },
        missingIP: {
            name: "Missing IP",
            description: "Incomplete IP records",
            count: 32,
            priority: "high",
            items: []
        },
        adminTidy: {
            name: "Admin Tidy",
            description: "Administrative cleanup required",
            count: 15,
            priority: "low",
            items: []
        },
        abionCountrySplit: {
            name: "Abion Country Split",
            description: "Regional trademark distribution",
            count: 23,
            priority: "medium",
            items: []
        },
        aiNoTrademarks: {
            name: ".ai No Trademarks",
            description: "AI companies without trademark protection",
            count: 56,
            priority: "high",
            items: []
        },
        representativeOverload: {
            name: "Representative Overload",
            description: "Cases with multiple representatives",
            count: 18,
            priority: "medium",
            items: []
        },
        highBrandRisk: {
            name: "High Brand Risk",
            description: "Companies with significant brand risk",
            count: 34,
            priority: "high",
            items: []
        },
        highValuation: {
            name: "High Valuation Impact",
            description: "Companies with significant valuation impact",
            count: 41,
            priority: "high",
            items: []
        },
        maMarket: {
            name: "M&A Market",
            description: "Merger & Acquisition opportunities",
            count: 27,
            priority: "medium",
            items: []
        },
        uk008009: {
            name: "UK008/9s",
            description: "UK trademark classifications 008/009",
            count: 63,
            priority: "medium",
            items: []
        },
        chineseApplicants: {
            name: "Chinese Applicants UK",
            description: "Chinese companies with UK marks",
            count: 49,
            priority: "medium",
            items: []
        },
        knownContacts: {
            name: "Known Contacts - Nurture",
            description: "Existing relationships to develop",
            count: 92,
            priority: "high",
            items: []
        }
    };
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedBucket, setSelectedBucket] = useState<any>(null);
    const [emailTemplate, setEmailTemplate] = useState("introduction");
    const [customizedEmail, setCustomizedEmail] = useState("");

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case "high":
                return "bg-[#fee2e2] text-[#991b1b]";
            case "medium":
                return "bg-[#fef9c3] text-[#854d0e]";
            case "low":
                return "bg-[#d1fae5] text-[#065f46]";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };
    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Top Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Total Active Cases</p>
                                <p className="text-2xl font-bold">523</p>
                            </div>
                            <Users className="w-8 h-8 text-gray-400" />
                        </div>
                    </div>
                </div>
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">High Priority</p>
                                <p className="text-2xl font-bold">147</p>
                            </div>
                            <AlertTriangle className="w-8 h-8 text-[#f87171]" />
                        </div>
                    </div>
                </div>
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Pending Renewals</p>
                                <p className="text-2xl font-bold">45</p>
                            </div>
                            <RefreshCw className="w-8 h-8 text-blue-400" />
                        </div>
                    </div>
                </div>
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500">Action Required</p>
                                <p className="text-2xl font-bold">89</p>
                            </div>
                            <Flag className="w-8 h-8 text-[#facc15]" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Strategy Buckets */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl lg:col-span-2'>
                    <div>
                        <div><span className='font-bold'>CRM Strategy Buckets</span></div>
                        <div>Manage and track different strategic initiatives</div>
                    </div>
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            {Object.entries(crmBuckets).map(([key, bucket]) => (
                                <div
                                    key={key}
                                    className="p-4 border rounded-lg cursor-pointer hover:bg-slate-50"
                                    onClick={() => setSelectedBucket(bucket)}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-medium">{bucket.name}</h3>
                                        <div className={`text-xs flex items-center font-medium me-2 px-2.5 py-0.5 rounded-sm ${getPriorityColor(bucket.priority)}`}>
                                            {bucket.count}
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500">{bucket.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Selected Bucket Details */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div>
                            <span className='font-bold'> {selectedBucket ? selectedBucket.name : "Bucket Details"}</span>
                        </div>
                        <div>
                            {selectedBucket ? selectedBucket.description : "Select a bucket to view details"}
                        </div>
                    </div>
                    <div>
                        {selectedBucket ? (
                            <div className="space-y-4 mt-2">
                                <div className="flex items-center justify-between">
                                    <div className={`text-xs flex items-center font-medium me-2 px-2.5 py-0.5 rounded-sm ${getPriorityColor(selectedBucket.priority)}`}>
                                        {selectedBucket.priority.toUpperCase()} Priority
                                    </div>
                                    <span className="text-sm text-gray-500">
                                        {selectedBucket.count} items
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {selectedBucket.items.map((item: any) => (
                                        <div key={item.id} className="p-3 border rounded-lg">
                                            <div className="font-medium">{item.name}</div>
                                            <div className="text-sm text-gray-500">
                                                {item.type} • {item.status}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="w-full flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">View All Items</button>
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 p-8">
                                Select a bucket to view its details and items
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Action Center */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div><span className='font-bold'>Action Center</span></div>
                    <div>Manage tasks and communications</div>
                </div>
                <div>
                    <div className="sm:hidden">
                        <label htmlFor="tabs" className="sr-only">Select your country</label>
                        <select className="focus:outline-none w-full rounded-2xl py-2 mb-3 px-4 border-4 border-main text-primary"
                            onChange={(e) => setActiveTab(e.target.value)}
                        >
                            <option value="tasks">Tasks</option>
                            <option value="emails">Emails</option>
                            <option value="reports">Reports</option>
                        </select>
                    </div>
                    <ul className="hidden text-sm font-medium text-center text-[#6B7280] rounded-lg shadow sm:flex">
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('tasks')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] rounded-s-lg focus:outline-none ${activeTab === 'tasks' ? 'active bg-[#F3F4F6] ' : 'bg-white'}`}>Tasks</a>
                        </li>
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('emails')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] focus:outline-none ${activeTab === 'emails' ? 'active bg-[#F3F4F6]' : 'bg-white'}`}>Emails</a>
                        </li>
                        <li className="w-full focus-within:z-10" onClick={() => setActiveTab('reports')}>
                            <a href="#" className={`inline-block font-bold w-full p-4 text-[#111827] border-r border-[#E5E7EB] focus:outline-none ${activeTab === 'reports' ? 'active bg-[#F3F4F6] ' : 'bg-white'}`}>Reports</a>
                        </li>
                    </ul>
                    <div className='mt-2'>
                    {activeTab === 'tasks' && (
                        <div className="space-y-4">
                            <input className='w-full border-4 border-[#033d44] p-2.5 text-black bg-[#fbfbfb] rounded-2xl mb-3 focus:outline-none' placeholder="Search tasks..." />
                            <div className="space-y-2">
                                {/* Task list would go here */}
                            </div>
                        </div>
                    )}
                    {activeTab === 'emails' && (
                        <div className="space-y-4">
                            <select className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary"
                                value={emailTemplate}
                                onChange={(e) => setEmailTemplate(e.target.value)}
                            >
                                <option value="">Select Email Template</option>
                                <option value="introduction">Introduction</option>
                                <option value="followUp">Follow Up</option>
                                <option value="renewal">Renewal Notice</option>
                                <option value="risk">Risk Assessment</option>
                            </select>
                            <textarea
                                value={customizedEmail}
                                onChange={(e) => setCustomizedEmail(e.target.value)}
                                placeholder="Email content..."
                                className='focus:outline-none shadow-xl mb-3 border-4 border-[#033d44] resize-none block p-2.5 w-full text-black bg-[#fbfbfb] rounded-2xl border focus:ring-mainBlue focus:border-mainBlue h-32'
                            />
                            <div className="flex justify-end gap-2">
                                <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">Save Draft</button>
                                <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">Send Email</button>
                            </div>
                        </div>
                    )}
                    {activeTab === 'reports' && (
                        <div className="space-y-4">
                            {/* Reports interface would go here */}
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
}
export default Main